/*******************************************************************
* PAGE        : ListComponent
* REMARKS     : Comman List View Component
* CREATED ON  : 03-11-2025
* AUTHOR      : Bamroliya.Paras
* MODIFY      : Bamroliya.Paras
* MODIFY ON   : 09-04-2026
********************************************************************/

import { Avatar, TablePagination, Tooltip } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AppConstant, chipStyleColors, CommonFormatDate, handleExportToExcel, IsEmpty } from "../../utils/AppCommonConstants";
import { Images } from "../../utils/Images";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import DropdownComponent from "../DropdownComponent/DropdownComponent";
import SkeletonComponent from "../SkeletonComponent/SkeletonComponent";
import TextFieldComponent from '../TextFieldComponent/TextFieldComponent';
import TitleComponent from "../TitleComponent/TitleComponent";
import './ListComponent.css';
import CheckboxComponent from "../CheckboxComponent/CheckboxComponent";
import { CustomPaginationActions } from "./CustomPaginationActions";
import PaginationComponent from "./PaginationComponent";
import SwitchComponent from "../SwitchComponent/SwitchComponent";
import ToggleSwitchComponent from "../SwitchComponent/ToggleSwitchComponent";
import ExportFieldSelection from "../ExportFieldSelection/ExportFieldSelection";

const ListComponent = ({
    PageTitle = "",
    gridColumns = [],
    gridData = [],
    headerButtons,
    isLoading = false,
    handleActionBtnClick,
    handleSelectValue,
    defaultChecked,
    onRowSelect,
    onGridSelectChange,
    hasprice = false,
    priceTotal,
    isCommanModal = true,
    allowSingleCheck = false,
    serverPagination = false, // when true, gridData is treated as ONE server-fetched page (no local slicing)
    totalRecords, // total record count across all server pages (needed for accurate page count under serverPagination)
    onPageChange }) => { // (pageNo, pageSize) - fired only when serverPagination is true and the user changes page/rows-per-page

    const [currentPage, setCurrentPage] = useState(1); // Start with 1
    const [gotoPage, setGotoPage] = useState(1); // Start with 1 for page input
    const [rowsPerPage, setRowsPerPage] = useState(AppConstant.DefaultRecordsPerPage);
    // const [filters, setFilters] = useState({});
    const [filters, setFilters] = useState(() => {
        const defaultFilters = {};

        gridColumns.forEach((item) => {
            if (Array.isArray(item.selectOption)) {
                const defaultOption = item.selectOption.find(
                    (option) => option.default === true
                );

                if (defaultOption) {
                    defaultFilters[item.name] = defaultOption.id;
                }
            }
        });

        return defaultFilters;
    });

    const [editable, setEditable] = useState(null);
    const [editableData, setEditableData] = useState(null);
    const [disabledFields, setDisabledFields] = useState([]);

    const [sortConfig, setSortConfig] = useState({ key: null, direction: AppConstant.SortDirection });

    const editableDataRef = useRef(editableData);

    const [rowDataChecked, setRowDataChecked] = useState({});
    useEffect(() => {
        setRowDataChecked(defaultChecked ? defaultChecked : {});
    }, [defaultChecked]);

    const [rowDataSwitch, setRowDataSwitch] = useState({});

    useEffect(() => {
        editableDataRef.current = editableData;
    }, [editableData]);







    // --- Column reorder / resize state ---
    // const [columnOrder, setColumnOrder] = useState(() => gridColumns.filter(Boolean).map(c => c.name));
    const [columnWidths, setColumnWidths] = useState({});
    const [dragOverColName, setDragOverColName] = useState(null);
    const dragColNameRef = useRef(null);
    const resizingRef = useRef(null); // { colName, startX, startWidth }
    const MIN_COLUMN_WIDTH = 60;

    // Stable string identifier for a column, used for ordering, keys, drag-and-drop,
    // resize, and freeze lookups. `name` may be a string or string[] (multi-field
    // columns) — arrays are joined into one deterministic string so identity never
    // depends on array reference equality.
    const getColKey = (col) => Array.isArray(col?.name) ? col.name.join('__') : String(col?.name);
    const [columnOrder, setColumnOrder] = useState(() => gridColumns.filter(Boolean).map(c => getColKey(c)));

    // Keep column order in sync if gridColumns set changes (add/remove), without losing user's custom order
    const columnNamesKey = gridColumns.filter(Boolean).map(c => getColKey(c)).join('|');
    useEffect(() => {
        const currentNames = columnNamesKey ? columnNamesKey.split('|') : [];
        setColumnOrder(currentNames);
        setColumnWidths({});
    }, [columnNamesKey]);

    const columnsByName = React.useMemo(() => {
        const map = {};
        gridColumns.forEach((c) => { if (c) map[getColKey(c)] = c; });
        return map;
    }, [gridColumns]);

    // Final render-ordered column list — use this instead of gridColumns for header/body rendering.
    // Frozen columns are always pushed to the end, regardless of where the sync effect
    // or drag-and-drop placed them in columnOrder — this is what keeps them pinned to
    // the right edge across route/column-set changes, without any index/route logic.
    const orderedColumns = React.useMemo(() => {
        const inOrder = columnOrder.map((name) => columnsByName[name]).filter(Boolean);
        const nonFrozen = inOrder.filter((col) => !col.freeze);
        const frozen = inOrder.filter((col) => col.freeze);
        return [...nonFrozen, ...frozen];
    }, [columnOrder, columnsByName]);

    // const getColumnWidth = (col) => {
    //     const custom = columnWidths[col.name];
    //     return custom ? `${custom}px` : (col.minWidth || '150px');
    // };

    const getColumnWidth = (col) => `${getColumnWidthPx(col)}px`;






    useEffect(() => {
        setCurrentPage(1);
        setGotoPage(1);
    }, [filters]);

    // Sorting function
    const handleSort = (key) => {
        let direction = 'ASC';
        if (sortConfig.key === key && sortConfig.direction === AppConstant.SortDirection) {
            direction = 'DESC';
        }
        setSortConfig({ key, direction });
    };

    // Filter the data based on applied filters
    const filteredData = gridData.filter((row) => {
        return gridColumns.every((col) => {
            if (!filters[col.name]) return true;

            // const filterValue = filters[col.name].toLowerCase();
            // const rowValue = row[col.name]?.toString().toLowerCase() || '';

            let filterValue = filters[col.name].toString().toLowerCase();
            let rowValue = row[col.name];

            if (typeof rowValue === "boolean") {
                rowValue = rowValue ? "yes" : "no";
            } else {
                rowValue = rowValue?.toString().toLowerCase() || "";
            }

            return rowValue.includes(filterValue);
        });
    });

    const sortedData = [...filteredData].sort((a, b) => {
        if (!sortConfig.key) return 0;

        const { key, direction } = sortConfig;

        const valueA = a[key];
        const valueB = b[key];

        let result = 0;

        if (typeof valueA === "string" && typeof valueB === "string") {
            result = valueA.localeCompare(valueB, undefined, {
                sensitivity: "base",
            });
        } else {
            result = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
        }

        return direction === "ASC" ? result : -result;
    });

    // Under serverPagination, gridData IS the current page already fetched from the API,
    // so the row count for THIS page can't tell us the grand total by itself.
    // Use the caller-supplied totalRecords when known; otherwise report only what's
    // actually been seen so far (no "+1 in case there's more" guessing - that lied
    // about the count, e.g. showing "11" when only 10 records exist).
    const maxRows = serverPagination
        ? (typeof totalRecords === 'number'
            ? totalRecords
            : (currentPage - 1) * (rowsPerPage === -1 ? sortedData.length : rowsPerPage) + sortedData.length)
        : sortedData.length;

    // const totalPages = Math.ceil(maxRows / rowsPerPage);  // Calculate total pages
    const totalPages = rowsPerPage === -1 ? 1 : Math.ceil(maxRows / rowsPerPage);

    useEffect(() => {
        // if current page becomes empty after delete
        // move automatically to previous page
        // (skipped under serverPagination: the parent, not local slicing, owns which page is valid)
        if (serverPagination) return;

        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
            setGotoPage(totalPages);
        }
        // if all records deleted
        if (totalPages === 0) {
            setCurrentPage(1);
            setGotoPage(1);
        }

    }, [totalPages, currentPage, serverPagination]);

    // Adjust the indices based on the current page
    const startIdx = (currentPage - 1) * rowsPerPage;  // Start index (0-based)
    const endIdx = startIdx + rowsPerPage; // End index

    // Slice the data to display based on current page.
    // Under serverPagination gridData is already just the current page, so it's shown as-is.
    const displayedData = serverPagination
        ? sortedData
        : (rowsPerPage === -1 ? sortedData : sortedData.slice(startIdx, endIdx));

    const handleChangePage = (event, newPage) => {
        setCurrentPage(newPage + 1);
        setGotoPage(newPage + 1);
        if (serverPagination) onPageChange?.(newPage + 1, rowsPerPage);
    };

    const handleGotoPageChange = (e) => {
        let value = parseInt(e.target.value, 10);
        if (value < 1) value = 1; // Prevent page 0 or negative
        if (value > totalPages) value = totalPages; // Prevent page numbers exceeding total pages
        setGotoPage(value);
    };

    const handleChangeRowsPerPage = (event) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setCurrentPage(1);
        setGotoPage(1);
        if (serverPagination) onPageChange?.(1, newRowsPerPage);
    };

    const handleSubmitGotoPage = (e) => {
        if (e.keyCode === 13) {
            if (gotoPage === "" || isNaN(gotoPage) || parseInt(gotoPage) <= 0) {
                setCurrentPage(1);
            } else {
                setCurrentPage(Math.abs(parseInt(gotoPage)));
            }
        };
    };

    const handleFilterInputChange = (event, colName) => {
        const { value } = event.target;

        setFilters((prevFilters) => ({
            ...prevFilters,
            [colName]: value,
        }));
    };

    const handleEditInputChange = (event, colName, rowData) => {
        const { value } = event.target;

        setEditableData((prevEditableData) => ({
            ...prevEditableData,
            [colName]: value,
        }));

    };
    const handleEditCheckboxChange = (event, colName, rowData) => {
        const isChecked = event.target.checked;
        setEditableData((prevEditableData) => ({
            ...prevEditableData,
            [colName]: isChecked,
        }));
    };

    const handleSaveEditbleRow = (item, rowIndex) => {
        handleActionBtnClick(item.id, rowIndex, editableDataRef.current);
        setEditable(null);
        setEditableData(null);
    };

    const handleEdit = (rowData, item) => {
        setDisabledFields(item.disabledFields)
        setEditable(rowData.id);
        setEditableData(rowData);
    };

    const handleCheckedAll = (e, permissionKey) => {
        const isChecked = e.target.checked;

        setRowDataChecked(prev => {
            const updated = { ...prev };
            displayedData.forEach(row => {
                // Check if the specific permission value is null
                const isDisabled = row[permissionKey] === null;

                // If it's disabled, do not update it
                if (isDisabled) {
                    updated[row.id] = {
                        row,  // Keep the full row
                        ...(updated[row.id] || {}),
                        [permissionKey]: updated[row.id]?.[permissionKey]  // Retain previous state
                    };
                } else {
                    // Update the permission state to the checked value for non-disabled rows
                    updated[row.id] = {
                        row,
                        ...(updated[row.id] || {}),
                        [permissionKey]: isChecked  // Set the state to checked/un-checked
                    };
                }
                handleActionBtnClick(permissionKey, row.id, updated);
            });

            return updated;
        });
    };

    const handleCheckBox = (e, rowData, key) => {
        const checked = e.target.checked;
        const rowId = rowData.id;

        setRowDataChecked(prev => {
            let updatedState = { ...prev };

            // Allow only one checked checkbox
            if (allowSingleCheck && checked) {
                const alreadyChecked = Object.keys(updatedState).some(
                    id => id !== String(rowId) && updatedState[id]?.[key]
                );

                if (alreadyChecked) {
                    handleActionBtnClick?.(key, rowId, updatedState, alreadyChecked);
                    return prev;
                }
            }

            updatedState[rowId] = {
                row: rowData,
                ...(updatedState[rowId] || {}),
                [key]: checked
            };

            handleActionBtnClick(key, rowId, updatedState);

            return updatedState;
        });
    };

    const handleGridSelectChange = (rowId, fieldName, value, rowData) => {
        if (onGridSelectChange) {
            onGridSelectChange(rowId, fieldName, value, rowData);
        }
    };

    const handleSwitch = (newValue, rowData, key) => {
        const rowId = rowData.id;

        setRowDataSwitch(prev => {
            const updatedState = {
                ...prev,
                [rowId]: {
                    row: rowData,
                    ...(prev[rowId] || {}),
                    [key]: newValue,
                },
            };

            handleActionBtnClick(key, rowId, updatedState);

            return updatedState;
        });
    };





    // --- Column drag-and-drop reorder ---
    // drag-and-drop: pass/compare keys, not raw name
    const handleColDragStart = (e, colKey) => {
        if (resizingRef.current) { e.preventDefault(); return; }
        if (columnsByName[colKey]?.freeze) { e.preventDefault(); return; }
        dragColNameRef.current = colKey;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', colKey);
    };

    const handleColDragOver = (e, colKey) => {
        if (columnsByName[colKey]?.freeze) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragColNameRef.current && dragColNameRef.current !== colKey) {
            setDragOverColName(colKey);
        }
    };

    const handleColDrop = (e, targetColKey) => {
        e.preventDefault();
        const sourceColKey = dragColNameRef.current;
        dragColNameRef.current = null;
        setDragOverColName(null);
        if (!sourceColKey || sourceColKey === targetColKey) return;
        if (columnsByName[sourceColKey]?.freeze || columnsByName[targetColKey]?.freeze) return;

        setColumnOrder((prevOrder) => {
            const frozenSlots = {};
            prevOrder.forEach((key, idx) => {
                if (columnsByName[key]?.freeze) frozenSlots[idx] = key;
            });
            const draggableKeys = prevOrder.filter((key) => !columnsByName[key]?.freeze);
            const fromIndex = draggableKeys.indexOf(sourceColKey);
            const toIndex = draggableKeys.indexOf(targetColKey);
            if (fromIndex === -1 || toIndex === -1) return prevOrder;
            draggableKeys.splice(fromIndex, 1);
            draggableKeys.splice(toIndex, 0, sourceColKey);
            const newOrder = [];
            let di = 0;
            for (let i = 0; i < prevOrder.length; i++) {
                newOrder.push(frozenSlots[i] ?? draggableKeys[di++]);
            }
            return newOrder;
        });
    };

    const handleColDragEnd = () => {
        dragColNameRef.current = null;
        setDragOverColName(null);
    };

    // --- Column resize ---
    // const handleResizeMouseMove = useCallback((e) => {
    //     if (!resizingRef.current) return;
    //     const { colName, startX, startWidth } = resizingRef.current;
    //     const newWidth = Math.max(MIN_COLUMN_WIDTH, Math.round(startWidth + (e.clientX - startX)));
    //     setColumnWidths((prev) => (prev[colName] === newWidth ? prev : { ...prev, [colName]: newWidth }));
    // }, []);

    const handleResizeMouseMove = useCallback((e) => {
        if (!resizingRef.current) return;
        const { colKey, startX, startWidth } = resizingRef.current;
        const newWidth = Math.max(MIN_COLUMN_WIDTH, Math.round(startWidth + (e.clientX - startX)));
        setColumnWidths((prev) => (prev[colKey] === newWidth ? prev : { ...prev, [colKey]: newWidth }));
    }, []);

    const handleResizeMouseUp = useCallback(() => {
        resizingRef.current = null;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        document.removeEventListener('mousemove', handleResizeMouseMove);
        document.removeEventListener('mouseup', handleResizeMouseUp);
    }, [handleResizeMouseMove]);

    const handleResizeMouseDown = useCallback((e, col) => {
        e.preventDefault();
        e.stopPropagation(); // prevent triggering th's onClick (sort) / drag
        const thElement = e.currentTarget.parentElement;
        const startWidth = thElement ? thElement.getBoundingClientRect().width : (parseInt(col.minWidth, 10) || 150);
        // resizingRef.current = { colName: col.name, startX: e.clientX, startWidth };
        resizingRef.current = { colKey: getColKey(col), startX: e.clientX, startWidth };
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
        document.addEventListener('mousemove', handleResizeMouseMove);
        document.addEventListener('mouseup', handleResizeMouseUp);
    }, [handleResizeMouseMove, handleResizeMouseUp]);

    useEffect(() => {
        // cleanup on unmount in case a resize was in progress
        return () => {
            document.removeEventListener('mousemove', handleResizeMouseMove);
            document.removeEventListener('mouseup', handleResizeMouseUp);
        };
    }, [handleResizeMouseMove, handleResizeMouseUp]);




    const tableScrollRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        const updateWidth = () => {
            if (tableScrollRef.current) setContainerWidth(tableScrollRef.current.clientWidth);
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    // numeric px width for a column (used for sticky left offset math)
    const getColumnWidthPx = (col) => {
        const custom = columnWidths[getColKey(col)];
        if (custom) return Math.max(custom, MIN_COLUMN_WIDTH);

        const mw = col.minWidth;
        let computed;
        if (typeof mw === 'string') {
            if (mw.trim().endsWith('%')) {
                const pct = parseFloat(mw);
                computed = containerWidth ? Math.round((pct / 100) * containerWidth) : 150;
            } else {
                computed = parseInt(mw, 10) || 150; // handles "150px" etc.
            }
        } else if (typeof mw === 'number') {
            computed = mw;
        } else {
            computed = 150;
        }

        // Never let a column render narrower than the app's own resize floor —
        // tiny percentage minWidths (e.g. "2%") would otherwise clip header labels/icons.
        return Math.max(computed, MIN_COLUMN_WIDTH);
    };

    // left offset (px) for every frozen column = sum of widths of frozen columns before it
    // left offsets — only for a LEADING run of frozen columns (from the start)
    const frozenLeftOffsets = React.useMemo(() => {
        const offsets = {};
        let runningLeft = 0;
        for (const col of orderedColumns) {
            if (!col.freeze) break; // stop at first non-frozen column
            offsets[getColKey(col)] = runningLeft;
            runningLeft += getColumnWidthPx(col);
        }
        return offsets;
    }, [orderedColumns, columnWidths]);

    // right offsets — only for a TRAILING run of frozen columns (from the end)
    const frozenRightOffsets = React.useMemo(() => {
        const offsets = {};
        let runningRight = 0;
        for (let i = orderedColumns.length - 1; i >= 0; i--) {
            const col = orderedColumns[i];
            if (!col.freeze) break; // stop at first non-frozen column (scanning backwards)
            offsets[getColKey(col)] = runningRight;
            runningRight += getColumnWidthPx(col);
        }
        return offsets;
    }, [orderedColumns, columnWidths]);

    const getFrozenStyle = (col, zIndex) => {
        if (!col?.freeze) return {};
        if (frozenLeftOffsets[getColKey(col)] !== undefined) {
            return { position: 'sticky', left: frozenLeftOffsets[getColKey(col)], zIndex };
        }
        if (frozenRightOffsets[getColKey(col)] !== undefined) {
            return { position: 'sticky', right: frozenRightOffsets[getColKey(col)], zIndex };
        }
        return {}; // frozen column not touching either edge — no reliable sticky side, falls back to normal scroll
    };

    const getFrozenClass = (col) => {
        if (!col?.freeze) return '';
        if (frozenLeftOffsets[getColKey(col)] !== undefined) return 'frozen-column frozen-left';
        if (frozenRightOffsets[getColKey(col)] !== undefined) return 'frozen-column frozen-right';
        return '';
    };

    const totalTableWidth = React.useMemo(() => {
        return orderedColumns.reduce((sum, col) => sum + getColumnWidthPx(col), 0);
    }, [orderedColumns, columnWidths]);

    const tableStyle = (containerWidth > 0 && totalTableWidth > containerWidth)
        ? { width: totalTableWidth, tableLayout: 'fixed' }
        : { width: '100%', tableLayout: 'fixed' };

    // const tableStyle = containerWidth > 0
    //     ? { width: Math.max(containerWidth, totalTableWidth), tableLayout: 'fixed' }
    //     : { width: '100%', tableLayout: 'fixed' }; // before first measurement only


    // Row status  →  toggle value
    const STATUS_TO_VALUE = {
        Approved: 'yes',
        Rejected: 'no',
        Pending: 'default',
    };


    const [showExportPopup, setShowExportPopup] = useState(false);

    const handleExportClick = () => {
        if (!filteredData || filteredData.length === 0) {
            alert("No data available to export.");
            return;
        }

        setShowExportPopup(true);
    };

    const handleFieldExport = selectedFields => {
        handleExportToExcel(
            filteredData,
            selectedFields,
            "Employee_",
            ".xlsx"
        );

        setShowExportPopup(false);
    };

    const renderActionButtons = useCallback((item, rowIndex, rowData) => {
        const iconColor = item.color || '#114d8b';
        const iconSize = item.size || 'small';
        const disabled = item.disabled ? true : false;
        const padding = '3px'

        const isDisabled = rowData[item.checkBoxKey] === null;
        const isSwitchDisabled = item.disabled || rowData.status !== "Pending";

        const switchValue = rowDataSwitch?.[rowData.id]?.[item.switchKey]
            ?? STATUS_TO_VALUE[rowData.status]
            ?? 'default'

        // console.log('switchValue:', switchValue, item, rowData)

        switch (item.name) {
            case "CheckBox":
                return (
                    <div style={{ display: 'flex', alignItems: 'center', padding: padding }}>
                        <CheckboxComponent
                            disabled={isDisabled || disabled}

                            id={`${rowData.id}_${item.checkBoxKey}_${item.id}`}
                            checked={Boolean(rowDataChecked?.[rowData.id]?.[item.checkBoxKey])}
                            onChange={(e) => {
                                handleCheckBox(e, rowData, item.checkBoxKey)
                            }}
                            value={rowData.ID}
                        />
                    </div>
                );
            case "Switch":
                return (
                    <ToggleSwitchComponent
                        value={switchValue}
                        onChange={(newValue) => handleSwitch(newValue, rowData, item.switchKey)}
                        disabled={isSwitchDisabled}
                        size="sm"
                        noLabel={item.noLabel}
                        defaultLabel={item.defaultLabel}
                        yesLabel={item.yesLabel}
                    />
                );
            case "Edit":
                return (
                    !item.isInlineEdit ? (
                        <img src={Images.editicon} alt={item.name} onClick={() => !disabled && handleActionBtnClick(item.id, rowIndex, rowData)}
                            style={disabled ? { padding: padding, cursor: 'not-allowed' } : { padding: padding, cursor: 'pointer' }} />
                    ) : editable === rowData.id ? (
                        <img src={Images.addtick} alt={item.name} onClick={() => !disabled && handleSaveEditbleRow(item, rowIndex)}
                            style={disabled ? { padding: padding, cursor: 'not-allowed' } : { padding: padding, cursor: 'pointer' }} />
                    ) : (
                        <img src={Images.editicon} alt={item.name} onClick={() => !disabled && handleEdit(rowData, item)}
                            style={disabled ? { padding: padding, cursor: 'not-allowed' } : { padding: padding, cursor: 'pointer' }} />
                    )
                );
            case "Delete":
                return (
                    <img src={Images.delete} alt={item.name} onClick={() => !disabled && handleActionBtnClick(item.id, rowIndex, rowData)}
                        style={disabled ? { padding: padding, cursor: 'not-allowed' } : { padding: padding, cursor: 'pointer' }} />
                );
            case "Assign":
                return (
                    <img src={Images.ticksquare} alt={item.name} onClick={() => !disabled && handleActionBtnClick(item.id, rowIndex, rowData)}
                        style={disabled ? { padding: padding, cursor: 'not-allowed' } : { padding: padding, cursor: 'pointer' }} />
                );
            case "Close":
                return (
                    <img src={Images.closesquare} alt={item.name} onClick={() => !disabled && handleActionBtnClick(item.id, rowIndex, rowData)}
                        style={disabled ? { padding: padding, cursor: 'not-allowed' } : { padding: padding, cursor: 'pointer' }} />
                );
            case "Approved":
                return (
                    <img src={Images.tickCircle} alt={item.name} onClick={() => !disabled && handleActionBtnClick(item.id, rowIndex, rowData)}
                        style={disabled ? { padding: padding, cursor: 'not-allowed' } : { padding: padding, cursor: 'pointer' }} />
                );
            case "Rejected":
                return (
                    <img src={Images.closeCircle} alt={item.name} onClick={() => !disabled && handleActionBtnClick(item.id, rowIndex, rowData)}
                        style={disabled ? { padding: padding, cursor: 'not-allowed' } : { padding: padding, cursor: 'pointer' }} />
                );
            case "Copy":
                return (
                    <img src={Images.copy} alt={item.name} onClick={() => !disabled && handleActionBtnClick(item.id, rowIndex, rowData)}
                        style={disabled ? { padding: padding, cursor: 'not-allowed' } : { padding: padding, cursor: 'pointer' }} />
                );
            case "View":
                return (
                    <img src={Images.view} alt={item.name} onClick={() => !disabled && handleActionBtnClick(item.id, rowIndex, rowData)}
                        style={disabled ? { padding: padding, cursor: 'not-allowed' } : { padding: padding, cursor: 'pointer' }} />
                );
            default:
                return null;
        }
    }, [handleActionBtnClick, handleCheckBox, editable]);

    const renderColumnContent = (col, rowData, rowIndex) => {
        if (col.actionType == 1) {
            return (isLoading ? <SkeletonComponent /> :
                <div className="table-actions">
                    {col.actionButtons?.map((item, index) => {
                        let tooltipTitle = item.isInlineEdit && editable === rowData.id ? "Save" : item.name;
                        if (item.name === "Switch") {
                            const switchValue = rowDataSwitch?.[rowData.id]?.[item.switchKey]
                                ?? STATUS_TO_VALUE[rowData.status]
                                ?? 'default';
                            tooltipTitle = switchValue === 'yes'
                                ? (item.yesLabel || 'Yes')
                                : switchValue === 'no'
                                    ? (item.noLabel || 'No')
                                    : (item.defaultLabel || 'Default');
                        }
                        return (
                            <React.Fragment key={index}>
                                <Tooltip title={tooltipTitle} arrow>
                                    {renderActionButtons(item, rowIndex, rowData)}
                                </Tooltip>
                            </React.Fragment>
                        )
                    })}
                </div>
            );
        }

        if (col.actionType == 4) {
            return isLoading ? <SkeletonComponent /> : <Avatar style={{ width: "25px", height: "25px", margin: "auto" }} alt={rowData.name} src={rowData.avatar || 'default-avatar.jpg'} />;
        }

        if (col.actionType == 3) {
            return isLoading ? <SkeletonComponent /> : chipStyleColors.map((ele, ind) => {
                if (rowData[col.name] === ele.value) {
                    return (
                        <div key={ind} className="list-chip-div" style={{ textAlign: col.textAlign }}>
                            <p className="list-chip-lable" style={{ backgroundColor: ele.backgroundColor, width: col.width }}>
                                {IsEmpty(ele.value) ? "N/A" : ele.name}
                            </p>
                        </div>
                    );
                }
            });
        }

        if (col.actionType == 5) {
            return (isLoading ? <SkeletonComponent /> :
                <div className="table-flex">
                    <DropdownComponent
                        key={`${col.name}-${rowData.id}`}
                        label={col.label}
                        value={rowData[col.name] || ''}
                        options={col.selectOption}
                        // onChange={(e) => handleGridSelectChange(rowData.id, col.name, e.target.value)}
                        onChange={(e) => {
                            e.stopPropagation();
                            handleGridSelectChange(rowData.id, col.name, e.target.value, rowData);
                        }}
                        style={{ margin: 0 }}
                    />
                    {/* <Tooltip title={"Assing"} arrow>
                        <img src={Images.ticksquare} alt={col.name} style={{ cursor: 'pointer'}} onClick={() => handleActionBtnClick(col.btnId, rowData.id, rowData)} />
                    </Tooltip> */}
                </div>
            );
        }

        // date
        if (col.actionType == 6) {
            return isLoading ? <SkeletonComponent /> :
                <Tooltip title={CommonFormatDate(rowData[col.name])} arrow>
                    {CommonFormatDate(rowData[col.name])}
                </Tooltip>
        }

        if (rowData?.id === editable) {
            if (disabledFields.includes(col.name)) {
                return (
                    <Tooltip title={rowData[col.name] || '-'} arrow>
                        {rowData[col.name] || '-'}
                    </Tooltip>)
            }
            return (isLoading ? <SkeletonComponent /> : <>
                {col.inlineEditActionType == 1
                    && <TextFieldComponent
                        disabled={disabledFields.includes(col.name)}
                        name={col.name}
                        value={editableData[col.name]}
                        onChange={(e) => handleEditInputChange(e, col.name, rowData)}
                        maxLength={col.maxLength || 25}
                        type={col.inputType || "text"}
                        allowDecimal={col.allowDecimal || false}
                        decimalScale={col.decimalScale || 3}
                        maxIntegerDigits={col.maxIntegerDigits || 18}
                    />}
                {col.inlineEditActionType == 2 &&
                    <CheckboxComponent
                        disabled={disabledFields.includes(col.name)}
                        name={col.name}
                        id={`header_${rowData?.id}`}
                        checked={Boolean(editableData[col.name])}
                        value={editableData[col.name]}
                        onChange={(e) => handleEditCheckboxChange(e, col.name, rowData)}
                    />}
                {col.inlineEditActionType == 3 &&
                    <DropdownComponent
                        name={col.name}
                        // value={editableData[col.name] || ''}
                        value={
                            rowData[col.selectOption]?.find(
                                option => option.label === (editableData[col.name] || rowData[col.name])
                            )?.id || (editableData[col.name] || rowData[col.name])
                        }
                        onChange={(e) => handleEditInputChange(e, col.name, rowData)}
                        id={`select${rowData?.id}`}
                        options={rowData[col.selectOption] || []}
                        menuStyle={{ maxHeight: "170px" }}
                        placeholder="select"
                    />}
            </>
            )
        }
        // rowData[col.name].toString() || '-';
        // String(rowData?.[col.name] ?? '-');
        // {rowData?.[col.name]?.toString() || '-'}
        const value = rowData?.[col.name];
        return isLoading
            ? <SkeletonComponent />
            : <Tooltip title={value?.toString()} arrow placement="bottom-start">
                <span style={{ cursor: (col.isClickable && value) ? "pointer" : "default" }}
                    onClick={() => (col.isClickable && value) && handleActionBtnClick(col.clickableId, rowData.id, rowData)}
                >
                    {value?.toString() || "-"}
                </span>
            </Tooltip>
    };

    // handle pagination if record more than 1000
    const getRowBackground = (row) => {
        if (Number(row.amount) > 100000) return "#ff6363"; // fff8e1
        if (row.ComplainStatus === "Reject" || row.ComplainStatus === "Rejected") return "#fff0f0";  // ffe2e2
        if (row.rowColor) { return "#D3E2F0" }
        return "white";
    };

    const getRowClass = (row) => {
        if (Number(row.amount) > 100000) return "high-amount";
        if (row.ComplainStatus === "Reject" || row.ComplainStatus === "Rejected") return "rejected";
        if (row.rowColor) return "highlight";
        return "";
    };

    return (
        <React.Fragment>
            <div className={"page-title-header"}>
                {PageTitle && <TitleComponent Title={PageTitle} />}
                <div className="header-actions">
                    {headerButtons?.map((item, index) => {
                        return (
                            <React.Fragment key={item.id + index}>
                                {item.id == 103 && Object.keys(rowDataChecked).filter(key => rowDataChecked[key].selected).length != 0 && <ButtonComponent color="primary" onClick={() => handleActionBtnClick(203, index, rowDataChecked)}>{item.name}</ButtonComponent>}

                                {item.id == 104 && Object.keys(rowDataChecked).filter(key => rowDataChecked[key].selected).length != 0 && <ButtonComponent color="primary" onClick={() => handleActionBtnClick(204, index, rowDataChecked)}>{item.name}</ButtonComponent>}

                                {item.id == 101 &&
                                    <>{item.type == "button"
                                        ? <ButtonComponent color="primary" icon={Images.addicon} onClick={() => handleActionBtnClick(201, 0, rowDataChecked)}>{item.name}</ButtonComponent>
                                        : <Tooltip title={item.name} arrow>
                                            <img src={Images.addsquare} alt={item.name} onClick={() => !item.disabled && handleActionBtnClick(item.id, 0, rowDataChecked)}
                                                style={{ width: '32px', height: '32px', cursor: item.disabled ? 'not-allowed' : 'pointer' }} />
                                        </Tooltip>}
                                    </>}

                                {item.id == 102 &&
                                    <Tooltip title={item.name} arrow>
                                        <img src={Images.downlod} alt={item.name} onClick={() => !item.disabled && handleExportClick()}
                                            style={{ width: '32px', height: '32px', cursor: item.disabled ? 'not-allowed' : 'pointer' }} />
                                    </Tooltip>}

                                {item.id == 105 &&
                                    <Tooltip title={item.name} arrow>
                                        <img src={Images.refresh} alt={item.name} onClick={() => !item.disabled && handleActionBtnClick(205, index, rowDataChecked)}
                                            style={{ width: '32px', height: '32px', cursor: item.disabled ? 'not-allowed' : 'pointer' }} />
                                    </Tooltip>}

                                {item.id == 106 && <ButtonComponent color="primary" onClick={() => handleActionBtnClick(203, index, rowDataChecked)}>{item.name}</ButtonComponent>}

                                {item.id == 107 && <DropdownComponent label={item.label} value={item.value} onChange={handleSelectValue} options={item.options} style={{ minWidth: item.minWidth }} />}
                            </React.Fragment>
                        )
                    })}
                </div>
            </div>

            <div className="table-wrapper">
                <div className="table-scroll-wrapper" ref={tableScrollRef} style={!isCommanModal ? { maxHeight: "500px" } : { minHeight: "0px" }}>
                    <table className="list-table-main" style={tableStyle}>
                        <thead>
                            {/* Label Panel */}
                            <tr style={{ backgroundColor: '#f0f0f0' }}>
                                {orderedColumns.map((col) => (
                                    <th
                                        key={col.name}
                                        className={`table-header-cell${dragOverColName === col.name ? ' drag-over' : ''} ${getFrozenClass(col)}`}
                                        style={{
                                            "--col-width": getColumnWidth(col),
                                            "--text-align": col.textAlign || "left",
                                            ...getFrozenStyle(col, 33),
                                        }}
                                        draggable={!col.disableReorder && !col.freeze}
                                        onDragStart={(e) => handleColDragStart(e, col.name)}
                                        onDragOver={(e) => handleColDragOver(e, col.name)}
                                        onDrop={(e) => handleColDrop(e, col.name)}
                                        onDragEnd={handleColDragEnd}
                                        onClick={() => (col.actionType == 2 || col.actionType == 6) && handleSort(col.name)}
                                    >
                                        <div className="th-content-row">
                                            <Tooltip title={col.label} arrow> {col.label} </Tooltip>
                                            {(col.actionType != 1 && col?.actionType != 3) && (
                                                <img src={Images.sortingarrow} className="table-header-sort-img" />
                                            )}
                                        </div>
                                        {(col.actionType != 1 && !col.disableResize) && (
                                            <span className="col-resize-handle" draggable={false} onMouseDown={(e) => handleResizeMouseDown(e, col)} onClick={(e) => e.stopPropagation()} />
                                        )}
                                    </th>
                                ))}
                            </tr>

                            {/* Filter Panel */}
                            <tr className="list-filter-tr">
                                {orderedColumns.map((item, ind) => {

                                    if (!item) return;

                                    const arrName = item.name;
                                    if (item.actionType == 1) {
                                        const checkBoxButton = item.actionButtons?.find(button => button.name === 'CheckBox');
                                        const checkBoxKey = checkBoxButton ? checkBoxButton.checkBoxKey : null;  // Safe fallback

                                        return (
                                            <th key={ind} className={getFrozenClass(item)} style={getFrozenStyle(item, 33)}>
                                                {/* <th key={ind} style={{ alignContent: 'center', textAlign: 'center', background: "red !important" }}> */}
                                                <div className="checkbox-custom-header">
                                                    {/* header Checkbox  */}
                                                    {!checkBoxButton ?
                                                        <img src={Images.filtersquare} alt="filtersquare" style={{ padding: '0px' }} />
                                                        : <CheckboxComponent
                                                            disabled={item.disabled}
                                                            id={`header_${checkBoxKey}`}
                                                            checked={displayedData.filter(row => row[checkBoxKey] !== null).every(row => rowDataChecked?.[row.id]?.[checkBoxKey])}  // Check only rows where the permission is not null
                                                            indeterminate={displayedData.filter(row => row[checkBoxKey] !== null).some(row => rowDataChecked?.[row.id]?.[checkBoxKey]) && !displayedData.filter(row => row[checkBoxKey] !== null).every(row => rowDataChecked?.[row.id]?.[checkBoxKey])}
                                                            onChange={(e) => handleCheckedAll(e, checkBoxKey)}
                                                        />}
                                                </div>
                                            </th>
                                        );
                                    } else if (item.actionType == 2) {
                                        return (
                                            <th key={ind} className={getFrozenClass(item)} style={getFrozenStyle(item, 33)}>
                                                <input
                                                    placeholder={`Search ${item.label}`}
                                                    maxLength={50}
                                                    type="text"
                                                    style={{ cursor: item.disabledSearch ? "not-allowed" : "pointer" }}
                                                    name={arrName}
                                                    value={filters[arrName] || ''}
                                                    onChange={(e) => handleFilterInputChange(e, arrName)}
                                                />
                                            </th>
                                        );
                                    } else if (item.actionType == 3) {
                                        return (
                                            <th key={ind} className={getFrozenClass(item)} style={getFrozenStyle(item, 33)}>
                                                <DropdownComponent
                                                    name={arrName}
                                                    value={filters[arrName] || ''}
                                                    onChange={(e) => handleFilterInputChange(e, arrName)}
                                                    id={`select${ind}`}
                                                    options={item.selectOption}
                                                    isForTableHeader={true}
                                                    menuStyle={{ maxHeight: "170px" }}
                                                />
                                            </th>
                                        );
                                    } else if (item.actionType == 6) {
                                        return (
                                            <th key={ind} className={getFrozenClass(item)} style={getFrozenStyle(item, 33)}>
                                                <div className="date-input-wrapper">
                                                    <input style={{ cursor: item.disabledSearch ? "not-allowed" : "pointer" }}
                                                        type="date"
                                                        name={arrName}
                                                        value={filters[arrName] || ''}
                                                        onChange={(e) => handleFilterInputChange(e, arrName)}
                                                        min={item.minDate}
                                                        max={item.maxDate} />
                                                    <img src={Images.calendar} alt="calendar" className="calendar-icon-inline" />
                                                </div>
                                            </th>
                                        );
                                    } else {
                                        return <th key={ind} className={getFrozenClass(item)} style={getFrozenStyle(item, 33)}></th>;
                                    }
                                })}
                            </tr>
                        </thead>

                        <tbody>
                            {isLoading
                                ? Array.from({ length: rowsPerPage }).map((_, rowIndex) => (
                                    <tr key={rowIndex}>
                                        {orderedColumns.map((col) => (
                                            <td
                                                key={col.name}
                                                className={`table-td-loading ${getFrozenClass(col)}`}
                                                style={{
                                                    maxWidth: col.minWidth || '150px',
                                                    textAlign: col.textAlign || 'left',
                                                    ...getFrozenStyle(col, 2),
                                                    ...(col.freeze ? { background: '#fff' } : {}),
                                                }}
                                            >
                                                {renderColumnContent(col, {}, rowIndex)}
                                            </td>
                                        ))}
                                    </tr>))
                                : !displayedData.length
                                    ? <tr>
                                        <td key={"No Record"} colSpan={gridColumns.length} className='table-td-no-record'>No Record Found</td>
                                    </tr>
                                    : displayedData.map((row, rowIndex) => (
                                        <tr
                                            key={`${row.id}-${rowIndex}`}
                                            onClick={() => onRowSelect?.(row)}
                                            // className={`table-row ${row.ComplainStatus === "Rejected" ? "rejected" : ""} ${row.rowClassName || ""}`}
                                            // style={{
                                            //     cursor: onRowSelect ? "pointer" : "default",
                                            //     backgroundColor: getRowBackground(row)
                                            // }}

                                            className={`table-row ${getRowClass(row)} ${row.rowClassName || ""}`}
                                            style={{ cursor: onRowSelect ? "pointer" : "default" }}
                                        >
                                            {orderedColumns.map((col, colIndex) => (
                                                <td
                                                    key={colIndex}
                                                    // className={`table-td-data ${getFrozenClass(col)}`}
                                                    className={`table-td-data ${getFrozenClass(col)} ${col.freeze ? "freeze" : ""}`}
                                                    style={{
                                                        maxWidth: col.minWidth || '150px',
                                                        textAlign: col.textAlign || 'left',
                                                        ...getFrozenStyle(col, 2),
                                                        // ...(col.freeze ? { background: getRowBackground(row) } : {}),
                                                    }}
                                                >
                                                    {renderColumnContent(col, row, rowIndex)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                        </tbody>
                    </table>

                    {!isLoading && hasprice &&
                        <div className="tab-header">
                            <div className="tab-title">
                            </div>
                            <div className="total-box">
                                <span>Total :</span>
                                <label>{priceTotal || 0}</label>
                            </div>
                        </div>}
                </div>
                <div className="table-pagination-wrapper">

                    {(!isLoading && displayedData.length > 0) &&
                        <TablePagination
                            rowsPerPageOptions={AppConstant.Pagination2}
                            component="div"
                            count={maxRows}
                            rowsPerPage={rowsPerPage}
                            page={currentPage - 1}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            id='page-box'
                            ActionsComponent={(subProps) => (
                                <PaginationComponent
                                    page={currentPage - 1}
                                    onPageChange={handleChangePage}
                                    totalPages={totalPages}
                                />
                            )}
                            className="custom-mui-pagination"
                        />}

                    {headerButtons?.map((item, index) => {
                        return (
                            <React.Fragment key={item.id + index}>
                                {item.id == 108 && Object.keys(rowDataSwitch).filter(key => rowDataSwitch[key].status).length != 0 && <ButtonComponent color="primary" onClick={() => handleActionBtnClick(108, index, rowDataSwitch)}>{item.name}</ButtonComponent>}
                            </React.Fragment>
                        )
                    })}

                    {/* {(!isLoading && displayedData.length > 0) && <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100, 200, 500, 1000, { label: "All", value: -1 }]}
                        component="div"
                        count={maxRows}
                        rowsPerPage={rowsPerPage}
                        page={currentPage - 1}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        id='page-box'
                        ActionsComponent={(subProps) => (
                            <CustomPaginationActions
                                {...subProps}
                                totalPages={totalPages}
                            />
                        )}
                        className="custom-mui-pagination"
                    />} */}

                    {/* <div style={{ fontSize: '13px', fontStyle: 'bold', marginLeft: "10px" }}>
                        Page: <input
                            className="goto-page-input"
                            type="number"
                            value={gotoPage}
                            disabled={totalPages <= 1 ? true : false}
                            onChange={handleGotoPageChange}
                            onKeyDown={handleSubmitGotoPage}
                        /> Of {totalPages}
                    </div> */}
                </div>
            </div>

            {showExportPopup && (
                <ExportFieldSelection
                    data={filteredData}
                    onExport={handleFieldExport}
                    onClose={() => setShowExportPopup(false)}
                />
            )}
        </React.Fragment >
    );
};

export default ListComponent;
